import { Tabs, TabsProps } from 'antd';
import dayjs from 'dayjs';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { Typography } from 'src/components/common/Typography';
import { addGameSearchParams } from 'src/helpers/gameQueriesManager';
import { checkDeviceCompatibility, detectDevice, formatCurrency, getSkinId, setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useGTM } from 'src/hooks/useGTM';
import { MediaManager } from 'src/manager/MediaManager';
import { seacrhByGameNameRequest } from 'src/services/gameApi';
import { getHighestWins } from 'src/services/winApi';
import { openAuthModal } from 'src/store/auth-modal/actions';
import { openDialog } from 'src/store/dialog/actions';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import { setSelectedGame } from 'src/store/games/actions';
import { RootState } from 'src/types/store-types';
import { GTM_EVENTS, GTM_EVENT_CATEGORIES } from 'src/utils/constants';
import SwiperCore from 'swiper';
import 'swiper/css';
import 'swiper/css/effect-fade';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useGameAction } from '../../../hooks/useGameAction';
import './styles.scss';

enum EnumTimeRange {
  PAST_24_HOURS = 1,
  PAST_7_DAYS = 2,
  PAST_30_DAYS = 3,
  PAST_6_MONTHS = 4,
  ALL_TIME = 5,
}

export const HighestWinsTable: FC = () => {
  const { t } = useTranslation();
  const swiperRef = useRef<SwiperCore>();
  const [searchParams, setQueryParams] = useSearchParams();
  const { cookiesAccepted } = useSelector((state: RootState) => state.app);
  const { user, gamePreviewProducts } = useSelector((state: RootState) => state.user);
  const [wins, setWins] = useState<any>([]);
  const [_, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<any>({ label: 'last_24h', value: EnumTimeRange.PAST_24_HOURS });
  const hasParams = Array.from(searchParams.keys()).length;

  const skinId = getSkinId();
  const device = detectDevice();
  const deviceCompatibilityStatus = checkDeviceCompatibility().compatible;
  const { generalConfigs } = useSelector((state: RootState) => state.configs);

  const _openAuthModal = useConnectedAction(openAuthModal);
  const _selectedGame = useConnectedAction(setSelectedGame);
  const _openDialog = useConnectedAction(openDialog);

  const { shouldShowLimits, shouldShowKyc } = useGameAction();

  const { fireEvent } = useGTM({
    action: GTM_EVENTS.GAME_OPEN_CLICK,
    event_category: GTM_EVENT_CATEGORIES.HIGHEST_WINDS_TABLE,
    event_label: 'Game open clicked from gighest wins',
  });

  const filterOptions = useMemo(
    () => [
      { label: 'last_24h', value: EnumTimeRange.PAST_24_HOURS },
      { label: 'last_7_days', value: EnumTimeRange.PAST_7_DAYS },
      { label: 'last_30_days', value: EnumTimeRange.PAST_30_DAYS },
      { label: 'last_6_months', value: EnumTimeRange.PAST_6_MONTHS },
      { label: 'all_time', value: EnumTimeRange.ALL_TIME },
    ],
    []
  );

  const tabItems: TabsProps['items'] = useMemo(
    () =>
      filterOptions.map((option) => ({
        key: String(option.value),
        label: t(option.label),
      })),
    [filterOptions]
  );

  useEffect(() => {
    if (swiperRef.current && hasParams === 0) {
      requestAnimationFrame(() => swiperRef.current?.autoplay?.start());
    }
  }, [hasParams, swiperRef]);

  const getPastDateRange = (range: EnumTimeRange): { dateFrom: string; dateTo: string } => {
    const now = new Date();
    const pattern = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';
    const nowFormatted = dayjs(now).format(pattern);

    let dateFrom: Date;

    switch (range) {
      case EnumTimeRange.PAST_24_HOURS:
        dateFrom = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case EnumTimeRange.PAST_7_DAYS:
        dateFrom = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case EnumTimeRange.PAST_30_DAYS:
        dateFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case EnumTimeRange.PAST_6_MONTHS:
        dateFrom = new Date(now);
        dateFrom.setMonth(now.getMonth() - 6);
        break;
      case EnumTimeRange.ALL_TIME:
        dateFrom = new Date(0);
    }

    return {
      dateFrom: dayjs(dateFrom).format(pattern),
      dateTo: nowFormatted,
    };
  };

  const getHighestWinsData = async (): Promise<any> => {
    setIsLoading(true);
    const range = getPastDateRange(filter.value);
    const res = await getHighestWins(skinId, range);

    if (res?.result?.data) {
      setWins(
        res.result.data.map((item: any, index: number) => ({
          ...item,
          position: index + 1, // position
          _uid: `${filter.value}-${index}-${item.gameId}-${item.username}`, // stable key
        }))
      );
      // Restart autoplay after next tick
      requestAnimationFrame(() => swiperRef?.current?.autoplay?.start());
    }
    setIsLoading(false);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const onGameClick = (id: number): void => {
    const data = { id, productId: '' };
    fireEvent({ game_id: id });

    if (
      process.env.REACT_APP_USE_COOKIES_GAMECARD === 'true' &&
      process.env.REACT_APP_USE_COOKIES === 'true' &&
      !cookiesAccepted
    ) {
      _openDialog({
        dialogType: EnumDialogsKeys.COOKIES_POPUP,
        dialogProps: { data: {} },
      });
    } else if (process.env.REACT_APP_DEVICE_COMPATIBILITY_ENABLE === 'true' && !deviceCompatibilityStatus) {
      _openDialog({
        dialogType: EnumDialogsKeys.DEVICE_COMPATIBILITY_POPUP,
        dialogProps: { data: {} },
      });
    } else if (shouldShowKyc) {
      _openDialog({
        dialogType: EnumDialogsKeys.KYC_POPUP,
        dialogProps: { data: {} },
      });
    } else if (shouldShowLimits) {
      _openDialog({
        dialogType: EnumDialogsKeys.SET_LIMITS_AFTER_LOGIN_POPUP,
        dialogProps: { data: {} },
      });
    } else {
      if (user) {
        setBodyOverflow('unset');
        if (generalConfigs?.gameLaunchPreview && gamePreviewProducts.includes(data.productId)) {
          _openDialog({
            dialogType: EnumDialogsKeys.GAME_LAUNCH_PREVIEW,
            dialogProps: { data: data },
          });
        } else {
          if (window.__gdlib && window.__gdlib['gamebox-widget_module']?.isGameBoxReady) {
            console.log('Success! The game has been opened.');
          } else {
            return;
          }

          seacrhByGameNameRequest(device, null, null, null, id).then((gamesInfo) => {
            if (!gamesInfo?.success || !gamesInfo?.games?.length) return;

            const gameInfo = gamesInfo.games[0];
            _selectedGame(gameInfo);
            addGameSearchParams(searchParams, setQueryParams, gameInfo);
          });
        }
      } else {
        setBodyOverflow('unset');
        _openAuthModal('login');
      }
    }
  };

  useEffect(() => {
    getHighestWinsData().catch((e) => console.error(e));
  }, [filter]);

  useEffect(() => {
    return () => {
      if (swiperRef.current) {
        try {
          swiperRef.current.autoplay?.stop();
          swiperRef.current.destroy(true, true);
        } catch (e) {
          console.error(e);
        }
        swiperRef.current = undefined;
      }
    };
  }, []);

  return (
    <div className="highest-wins-container">
      <div className="highest-wins-header">
        <Typography className="highest-wins-title" variant="h3">
          {t('highestWins')}
        </Typography>
        <Tabs
          className="highest-wins-filter"
          activeKey={String(filter.value)}
          type={'card'}
          items={tabItems}
          animated
          tabPosition="top"
          onChange={(key) => {
            const selected = filterOptions.find((option) => String(option.value) === key);

            if (selected) {
              setFilter(selected);
            }
          }}
        />
      </div>
      {wins && wins.length && (
        <div
          onMouseEnter={() => {
            if (swiperRef.current) {
              requestAnimationFrame(() => swiperRef.current?.autoplay?.stop());
            }
          }}
          onMouseLeave={() => {
            if (swiperRef.current && hasParams === 0) {
              requestAnimationFrame(() => swiperRef.current?.autoplay?.start());
            }
          }}
        >
          <Swiper
            onSwiper={(swiper: any) => (swiperRef.current = swiper)}
            modules={[Autoplay]}
            slidesPerView={isDesktop ? 5.7 : 1.7}
            slidesPerGroup={1}
            spaceBetween={12}
            slidesOffsetBefore={isDesktop ? 5 : 10}
            speed={3000}
            loop
            autoplay={{
              delay: 0,
              pauseOnMouseEnter: true,
              disableOnInteraction: false,
            }}
          >
            {wins.map(
              (item: {
                _uid: string;
                username: string;
                won: number;
                currency: string;
                gameId: number;
                gameName: string;
              }) => (
                <SwiperSlide key={item._uid} style={{ width: 260 }}>
                  <div className="highest-win-card" onClick={() => onGameClick(item.gameId)}>
                    <div className="highest-win-header">
                      <div className="avatar">
                        <SvgIcon src={MediaManager.getSrcFromMediaAssets('yellowUser.svg', '/settings/')} />
                      </div>
                      <div className="username">{item.username}</div>
                    </div>
                    <div className="won-amount">+{formatCurrency(item.won, item.currency)}</div>
                    <div className="game-name">{item.gameName}</div>
                  </div>
                </SwiperSlide>
              )
            )}
          </Swiper>
        </div>
      )}
      {!wins.length && (
        <>
          <div className="highest-wins-empty">{t('no_data')}</div>
          <div className="highest-wins-separator" />
        </>
      )}
    </div>
  );
};

export default HighestWinsTable;
